import { useState } from 'react';
import { Table, Button, Tag, Typography, Select, Modal, Descriptions, Tooltip, Popconfirm } from 'antd';
import { EyeOutlined, SearchOutlined, FilterOutlined, ShoppingOutlined } from '@ant-design/icons';
import { XCircle } from '@phosphor-icons/react';
import { useOrders, Order } from '@hooks';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

export const OrdersManagementPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const { orders, cancelOrder } = useOrders({ enableOrdersFetch: true });

  const filteredOrders = (orders.data || []).filter((order) => {
    let matchesSearch = true;
    let matchesStatus = true;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      matchesSearch = 
        order.orderNumber.toLowerCase().includes(query) ||
        order.customerEmail.toLowerCase().includes(query);
    }

    if (statusFilter) {
      matchesStatus = order.status.toLowerCase() === statusFilter.toLowerCase();
    }

    return matchesSearch && matchesStatus;
  });

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'gold',
      paid: 'green',
      processing: 'blue',
      shipped: 'purple',
      delivered: 'cyan',
      cancelled: 'red',
    };
    return colors[status.toLowerCase()] || 'default';
  };

  const handleViewOrder = (order: Order) => {
    setSelectedOrder(order);
    setIsModalVisible(true);
  };

  const handleCancelOrder = async (orderId: string) => {
    try {
      await cancelOrder.mutateAsync(orderId);
    } catch (error) {
      console.error('Failed to cancel order:', error);
    }
  };

  const columns = [
    {
      title: <span className="font-azonix text-primary">ORDER NUMBER</span>,
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      sorter: true,
      render: (orderNumber: string) => (
        <span className="font-coda font-normal text-primary">{orderNumber}</span>
      ),
    },
    {
      title: <span className="font-azonix text-primary">CUSTOMER EMAIL</span>,
      dataIndex: 'customerEmail',
      key: 'customerEmail',
      sorter: true,
      render: (email: string) => (
        <span className="font-coda">{email}</span>
      ),
    },
    {
      title: <span className="font-azonix text-primary">DATE</span>,
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      render: (date: Date) => (
        <span className="font-coda">{dayjs(date).format('MMM DD, YYYY HH:mm')}</span>
      ),
    },
    {
      title: <span className="font-azonix text-primary">ITEMS</span>,
      dataIndex: 'items',
      key: 'items',
      sorter: true,
      render: (items: any[]) => (
        <span className="font-coda">{items?.length || 0}</span>
      ),
    },
    {
      title: <span className="font-azonix text-primary">TOTAL</span>,
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      sorter: true,
      render: (total: number) => (
        <span className="font-coda text-primary">{formatPrice(total)}</span>
      ),
    },
    {
      title: <span className="font-azonix text-primary">STATUS</span>,
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      render: (status: string) => (
        <Tag color={getStatusColor(status)} className="font-coda uppercase">
          {status}
        </Tag>
      ),
    },
    {
      title: <span className="font-azonix text-primary">ACTIONS</span>,
      key: 'actions',
      width: 150,
      render: (_: any, record: Order) => (
        <div className="flex gap-2">
          <Tooltip title="View details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleViewOrder(record);
              }}
              className="text-primary hover:[&_svg]:transition-all hover:[&_svg]:duration-200 hover:[&_svg]:scale-110 hover:[&_svg]:drop-shadow-[0_4px_8px_#00ade6]"
            />
          </Tooltip>
          <Tooltip title="Cancel order">
            <Popconfirm
              title={<span className="font-azonix !font-normal">Cancel Order</span>}
              description="Are you sure you want to cancel this order? This action cannot be undone."
              onConfirm={() => handleCancelOrder(record.id)}
              okText="Yes, cancel"
              cancelText="No"
              overlayClassName="remove-position-popconfirm"
              okButtonProps={{ className: "btn-single-cut-cancel", danger: true }}
              cancelButtonProps={{ className: "btn-single-cut" }}
              disabled={record.status.toLowerCase() === 'cancelled'}
            >
              <Button
                type="text"
                danger
                icon={<XCircle size={16} />}
                onClick={(e) => e.stopPropagation()}
                disabled={record.status.toLowerCase() === 'cancelled'}
                className="!text-orange-500 hover:[&_svg]:transition-all hover:[&_svg]:duration-200 hover:[&_svg]:scale-110 hover:[&_svg]:drop-shadow-[0_4px_8px_#f97316] disabled:!text-zinc-600 disabled:cursor-not-allowed"
              />
            </Popconfirm>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="estabrook-bg pt-25 pb-10 md:pb-20">
      {/* Hero Section */}
      <section className="w-full h-[400px]">
        <div className="container h-full flex flex-col justify-center items-center mx-auto">
          <Title level={1} className="font-azonix !font-normal text-primary text-center tracking-wider pb-4 !mb-0">
            ORDERS MANAGEMENT
          </Title>
          <div className="max-w-3xl mx-auto !mb-8">
            <Paragraph className="text-center text-lg text-light">
              View and manage customer orders
            </Paragraph>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="w-full py-12 bg-charcoal">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto px-6 py-8">
            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-4 mb-6 items-center justify-between">
              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                {/* Custom Search Input */}
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <SearchOutlined className="text-primary" style={{ color: '#00ade6' }} />
                  </div>
                  <input
                    type="text"
                    placeholder="Search by order number or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full sm:w-80 h-11 pl-11 pr-4 bg-zinc-800/80 border border-zinc-700 text-white placeholder-zinc-500 font-coda text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/50 transition-all duration-200"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-zinc-500 hover:text-white transition-colors cursor-pointer"
                    >
                      ×
                    </button>
                  )}
                </div>
                
                {/* Status Select */}
                <Select
                  placeholder={
                    <span className="flex items-center gap-2 text-zinc-500">
                      <FilterOutlined className="text-primary" style={{ color: '#00ade6' }} /> Status
                    </span>
                  }
                  allowClear={!!statusFilter}
                  value={statusFilter || undefined}
                  onChange={(value) => setStatusFilter(value || '')}
                  className={`w-full sm:w-48 [&_.ant-select-selector]:!bg-zinc-800/80 [&_.ant-select-selector]:!border-zinc-700 [&_.ant-select-selector]:!h-11 [&_.ant-select-selection-placeholder]:!text-zinc-500 [&_.ant-select-selection-item]:!text-white [&_.ant-select-arrow]:!text-[#00ade6] [&_.ant-select-clear]:!bg-zinc-800 [&_.ant-select-clear]:!text-[#00ade6] [&_.ant-select-clear_svg]:!fill-[#00ade6] [&_.ant-select-clear]:!top-[55%] ${statusFilter ? '[&_.ant-select-arrow]:!hidden' : '[&_.ant-select-arrow]:!top-[55%] [&_.ant-select-arrow_svg]:!fill-[#00ade6]'}`}
                  size="large"
                  popupClassName="!bg-zinc-800 !border-zinc-700 [&_.ant-select-item]:!text-zinc-300 [&_.ant-select-item-option-active]:!bg-zinc-700 [&_.ant-select-item-option-selected]:!bg-primary/20 [&_.ant-select-item-option-selected]:text-primary"
                >
                  <Option value="pending">Pending</Option>
                  <Option value="paid">Paid</Option>
                  <Option value="processing">Processing</Option>
                  <Option value="shipped">Shipped</Option>
                  <Option value="delivered">Delivered</Option>
                  <Option value="cancelled">Cancelled</Option>
                </Select>
              </div>
              
              {/* Stats */}
              <div className="flex items-center gap-2 bg-zinc-800/50 px-4 py-2.5 border border-zinc-700">
                <ShoppingOutlined className="text-lg pb-1" style={{ color: '#00ade6' }} />
                <Text className="font-coda font-medium">
                  <span className="text-white text-sm">{filteredOrders.length}</span> <span className="text-white text-sm">orders</span>
                </Text>
              </div>
            </div>

            {/* Orders Table */}
            <div className="bg-zinc-900/50 border border-zinc-800 shadow-xl overflow-x-auto hide-scrollbar">
              <Table
                dataSource={filteredOrders}
                columns={columns}
                rowKey="id"
                loading={orders.isLoading}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showTotal: (total) => <span className="text-white tracking-wider">Total <span className="text-white font-coda">{total}</span> orders</span>,
                  className: "orders-pagination !bg-transparent [&_.ant-pagination-item]:!bg-[#333333] [&_.ant-pagination-item]:!border-[#AAAAAA] [&_.ant-pagination-item-active]:!bg-white [&_.ant-pagination-item-active]:!border-white [&_.ant-pagination-item_a]:!text-white [&_.ant-pagination-item-active_a]:!text-black [&_.ant-pagination-prev_.ant-pagination-item-link]:!bg-[#333333] [&_.ant-pagination-next_.ant-pagination-item-link]:!bg-[#333333] [&_.ant-pagination-prev_.ant-pagination-item-link]:!border-[#AAAAAA] [&_.ant-pagination-next_.ant-pagination-item-link]:!border-[#AAAAAA] [&_.ant-pagination-prev_.ant-pagination-item-link]:!text-white [&_.ant-pagination-next_.ant-pagination-item-link]:!text-white [&_.ant-pagination-options_.ant-select-selector]:!bg-white [&_.ant-pagination-options_.ant-select-selector]:!border-none [&_.ant-pagination-options_.ant-select-selector]:!shadow-none [&_.ant-pagination-options_.ant-select-selection-item]:!text-black [&_.ant-pagination-options_.ant-select-arrow]:!text-black",
                }}
                className="orders-table [&_.ant-table]:!bg-transparent [&_.ant-table-thead_th]:!bg-zinc-800/50 [&_.ant-table-thead_th]:!text-white [&_.ant-table-thead_th]:!border-b-zinc-700 [&_.ant-table-thead_th]:!font-coda [&_.ant-table-tbody_tr]:!border-b-zinc-800 [&_.ant-table-tbody_td]:!bg-transparent [&_.ant-table-tbody_td]:!border-b-zinc-700 [&_.ant-table-tbody_td]:!text-white [&_.ant-table-row:hover_td]:!bg-zinc-800/50 [&_.ant-table-placeholder]:!bg-transparent [&_.ant-empty-description]:!text-white min-w-[800px]"
              />
            </div>

            {/* Order Details Modal */}
            <Modal
              title={
                <Title level={4} className="font-azonix !font-normal text-primary mb-0">
                  Order Details - {selectedOrder?.orderNumber}
                </Title>
              }
              open={isModalVisible}
              onCancel={() => setIsModalVisible(false)}
              width={700}
              footer={[
                <Button key="close" onClick={() => setIsModalVisible(false)}>
                  Close
                </Button>,
              ]}
              styles={{
                content: { backgroundColor: '#18181b' },
                header: { backgroundColor: '#18181b', borderBottom: '1px solid #3f3f46' },
              }}
            >
              {selectedOrder && (
                <div className="mt-4">
                  <Descriptions column={2} className="order-descriptions">
                    <Descriptions.Item 
                      label={<span className="text-zinc-400 font-coda">Order Number</span>}
                      span={2}
                    >
                      <span className="font-coda text-primary">
                        {selectedOrder.orderNumber}
                      </span>
                    </Descriptions.Item>
                    <Descriptions.Item label={<span className="text-zinc-400 font-coda">Customer Email</span>}>
                      <span className="font-coda text-white">{selectedOrder.customerEmail}</span>
                    </Descriptions.Item>
                    <Descriptions.Item label={<span className="text-zinc-400 font-coda">Date</span>}>
                      <span className="font-coda text-white">
                        {dayjs(selectedOrder.createdAt).format('MMM DD, YYYY HH:mm')}
                      </span>
                    </Descriptions.Item>
                    <Descriptions.Item label={<span className="text-zinc-400 font-coda">Status</span>}>
                      <Tag color={getStatusColor(selectedOrder.status)} className="font-coda uppercase">
                        {selectedOrder.status}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label={<span className="text-zinc-400 font-coda">Total</span>}>
                      <span className="font-coda text-primary">
                        {formatPrice(selectedOrder.totalAmount)}
                      </span>
                    </Descriptions.Item>
                  </Descriptions>

                  <div className="mt-6 mb-8">
                    <Title level={5} className="font-azonix !font-normal text-white mb-4">
                      Order Items
                    </Title>
                    <div className="bg-zinc-800 overflow-hidden">
                      <div className="grid grid-cols-12 gap-2 bg-zinc-900 px-4 py-3 border-b border-primary">
                        <div className="col-span-6 font-coda text-primary uppercase text-sm">
                          Product
                        </div>
                        <div className="col-span-2 font-coda text-primary uppercase text-sm text-center">
                          Qty
                        </div>
                        <div className="col-span-2 font-coda text-primary uppercase text-sm text-right">
                          Price
                        </div>
                        <div className="col-span-2 font-coda text-primary uppercase text-sm text-right">
                          Subtotal
                        </div>
                      </div>
                      {selectedOrder.items.map((item, index) => {
                        // Build variant string (e.g., "M / Black")
                        const variantParts: string[] = [];
                        if (item.size) variantParts.push(item.size);
                        if (item.color) variantParts.push(item.color);
                        const variantString = variantParts.length > 0 ? ` (${variantParts.join(' / ')})` : '';
                        
                        return (
                          <div 
                            key={item.id}
                            className={`grid grid-cols-12 gap-2 px-4 py-3 ${
                              index < selectedOrder.items.length - 1 ? 'border-b border-zinc-700' : ''
                            }`}
                          >
                            <div className="col-span-6 font-coda text-white">
                              {item.productName}{variantString}
                            </div>
                          <div className="col-span-2 font-coda text-white text-center">
                            {item.quantity}
                          </div>
                          <div className="col-span-2 font-coda text-primary text-right">
                            {formatPrice(item.price)}
                          </div>
                          <div className="col-span-2 font-coda text-primary font-bold text-right">
                            {formatPrice(item.price * item.quantity)}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </Modal>
          </div>
        </div>
      </section>
    </div>
  );
};

